import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export default function Signup() {
  const navigate = useNavigate();
  const [userType, setUserType] = useState<'fan' | 'creator' | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUserTypeSelect = (type: 'fan' | 'creator') => {
    setUserType(type);
    setError('');
  };

  const validateBirthDate = (dateString: string): boolean => {
    if (!dateString) return true;
    
    const date = new Date(dateString);
    const today = new Date();
    const minDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());
    
    return date <= today && date >= minDate;
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Supabase Auth로 회원가입
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
            user_type: userType,
            birth_date: birthDate,
            invite_code: inviteCode || undefined,
          },
        },
      });

      if (authError) {
        alert(`회원가입 실패: ${authError.message}`);
        setLoading(false);
        return;
      }

      if (!authData.user) {
        alert('회원가입에 실패했습니다.');
        setLoading(false);
        return;
      }

      // 회원가입 성공 - 트리거가 자동으로 프로필 생성
      alert('회원가입이 완료되었습니다! 로그인 페이지로 이동합니다.');
      navigate('/login');
    } catch (error) {
      console.error('회원가입 오류:', error);
      alert('회원가입 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-pink-50 to-purple-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* 로고 */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-black bg-gradient-to-r from-pink-500 via-purple-500 to-blue-400 bg-clip-text text-transparent mb-2 tracking-tight" style={{ textShadow: '0 0 20px rgba(236, 72, 153, 0.3)' }}>
            MyFanHub
          </h1>
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <p className="text-sm">당신만의 팬 플랫폼</p>
            <div className="flex gap-1">
              <i className="ri-heart-fill text-pink-400 text-lg"></i>
              <i className="ri-star-fill text-yellow-400 text-lg"></i>
            </div>
          </div>
        </div>

        {/* 회원가입 폼 */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8 border border-pink-100">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">회원가입</h2>
          
          {/* 오류 메시지 */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-600 flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </p>
            </div>
          )}
          
          {!userType ? (
            <div className="space-y-4">
              <p className="text-center text-gray-600 mb-6">가입 유형을 선택해주세요</p>
              <button
                onClick={() => handleUserTypeSelect('fan')}
                className="w-full bg-gradient-to-r from-blue-400 to-purple-400 text-white py-4 rounded-xl font-semibold hover:from-blue-500 hover:to-purple-500 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-heart-line mr-2"></i>
                팬으로 가입
              </button>
              <button
                onClick={() => handleUserTypeSelect('creator')}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 text-white py-4 rounded-xl font-semibold hover:from-pink-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-star-line mr-2"></i>
                크리에이터로 가입
              </button>
            </div>
          ) : (
            <form onSubmit={handleSignup} className="space-y-5">
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-3 rounded-lg text-center mb-4">
                <p className="text-sm font-medium text-gray-700">
                  {userType === 'fan' ? '팬' : '크리에이터'} 가입
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">이름</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none transition-all text-sm"
                  placeholder="이름을 입력하세요"
                  required
                  disabled={loading}
                  minLength={2}
                  maxLength={50}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">생년월일</label>
                <input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none transition-all text-sm"
                  required
                  disabled={loading}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">이메일</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none transition-all text-sm"
                  placeholder="이메일을 입력하세요"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">비밀번호</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none transition-all text-sm"
                  placeholder="비밀번호를 입력하세요 (최소 6자)"
                  required
                  disabled={loading}
                  minLength={6}
                  maxLength={128}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  초대코드 <span className="text-gray-400 text-xs">(선택사항)</span>
                </label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none transition-all text-sm"
                  placeholder="초대코드가 있다면 입력하세요"
                  disabled={loading}
                  maxLength={20}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setUserType(null);
                    setInviteCode('');
                    setBirthDate('');
                    setError('');
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  이전
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white py-3 rounded-xl font-semibold hover:from-pink-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? '가입 중...' : '가입하기'}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-gray-600">
            이미 계정이 있으신가요?{' '}
            <Link to="/login" className="text-pink-500 font-semibold hover:text-pink-600 cursor-pointer">
              로그인
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
